// v3.18-1385 GMAIL-005 — Aggregate company-scoped Gmail communications.
// Rendered inside the Company Detail "Communications" tab. Backed by
// /api/companies/{id}/communications which is scoped by company_id AND
// filtered through can_user_access_client for any client-linked rows.
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Mail, RefreshCw, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';

const fmtWhen = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso.slice(0, 16); }
};

export default function CompanyCommunicationsPanel({ companyId }) {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/communications?limit=100`);
      setMsgs(data?.messages || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load company communications');
    } finally { setLoading(false); }
  }, [companyId, tick]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="company-gmail-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Mail className="w-4 h-4" /> Company Gmail activity
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs"
          data-testid="company-gmail-refresh"
          onClick={() => setTick((n) => n + 1)}>
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="p-8 text-center text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
        </div>
      ) : msgs.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500" data-testid="company-gmail-empty">
          No Gmail activity linked to this company yet.
        </div>
      ) : (
        <div className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
          {msgs.map((m) => (
            <div key={m.gmail_message_id} className="px-3 py-2.5 hover:bg-slate-50">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-slate-800 truncate">
                  {m.subject || '(no subject)'}
                </div>
                <div className="text-[10px] text-slate-500 whitespace-nowrap">
                  {fmtWhen(m.internal_date)}
                </div>
              </div>
              <div className="text-xs text-slate-500 truncate">
                {m.from} → {m.to}
              </div>
              <div className="text-xs text-slate-500 truncate mt-0.5">{m.snippet}</div>
              <div className="flex items-center gap-2 mt-1 text-[10px]">
                <span className="px-1.5 rounded bg-slate-100 uppercase">{m.folder}</span>
                {m.is_unread   && <span className="px-1.5 rounded bg-blue-100 text-blue-700">Unread</span>}
                {m.is_starred  && <span className="px-1.5 rounded bg-amber-100 text-amber-700">★</span>}
                {(m.attachments || []).length > 0 && (
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Paperclip className="w-3 h-3" /> {m.attachments.length}
                  </span>
                )}
                {m.client_id && (
                  <span className="ml-auto px-1.5 rounded bg-emerald-100 text-emerald-700">
                    linked client
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
